import { RenderContext } from '../render/context/createRenderContext';
import { Provider } from './provider';
import { createImageCacheKey, loadImage } from '../render/utils/image';
import { parseCSSURLString, resolveFillType } from '../render/utils/background';
import { FILL_TYPES } from '../render/constants';
import { isURLFill, parseURLFill } from '../render//utils/fill';

export interface ImageCacheItem {
  cacheKey: string;
  src: string;
  loading: boolean;
  error: string | null;
  img: HTMLImageElement | null;
  ready?: Promise<HTMLImageElement | null>;
  colors?: string[];
  svgContent?: string;
  dispose?: () => void;
}

export class ImageProvider extends Provider {
  /**
   * Map from node.id to image cache item
   */
  private _nodeImageCache: Map<string, ImageCacheItem>;

  constructor() {
    super();
    this._nodeImageCache = new Map();
  }

  flush() {
    if (this.disposed) return;
  }

  attach(renderContext: RenderContext) {
    super.attach(renderContext);
  }

  dispose() {
    // Dispose all cached images
    this._nodeImageCache.forEach((cache) => {
      if (cache.dispose) {
        cache.dispose();
      }
    });
    this._nodeImageCache.clear();
    super.dispose();
  }

  clear() {
    this._nodeImageCache.forEach((cache) => {
      if (cache.dispose) {
        cache.dispose();
      }
    });
    this._nodeImageCache.clear();
  }

  /**
   * Get image cache for a node
   * @param nodeId The node id
   * @returns The image cache item or undefined
   */
  getImageCache(nodeId: string): ImageCacheItem | undefined {
    return this._nodeImageCache.get(nodeId);
  }

  /**
   * Set image cache for a node
   * @param nodeId The node id
   * @param cache The image cache item
   */
  setImageCache(nodeId: string, cache: ImageCacheItem): void {
    // Dispose old cache if exists
    const oldCache = this._nodeImageCache.get(nodeId);
    if (oldCache && oldCache !== cache && oldCache.dispose) {
      oldCache.dispose();
    }
    this._nodeImageCache.set(nodeId, cache);
  }

  /**
   * Remove image cache for a node
   * @param nodeId The node id
   */
  removeImageCache(nodeId: string): void {
    const cache = this._nodeImageCache.get(nodeId);
    if (cache) {
      if (cache.dispose) {
        cache.dispose();
      }
      this._nodeImageCache.delete(nodeId);
    }
  }

  /**
   * Check if a node has image cache
   * @param nodeId The node id
   * @returns True if the node has image cache
   */
  hasImageCache(nodeId: string): boolean {
    return this._nodeImageCache.has(nodeId);
  }

  /**
   * Check if a node's image is loading
   * @param nodeId The node id
   * @returns True if the image is loading
   */
  isLoading(nodeId: string): boolean {
    const cache = this._nodeImageCache.get(nodeId);
    return cache?.loading ?? false;
  }

  /**
   * Check if a node's image has error
   * @param nodeId The node id
   * @returns True if the image has error
   */
  hasError(nodeId: string): boolean {
    const cache = this._nodeImageCache.get(nodeId);
    return cache?.error != null;
  }

  /**
   * Check if a node's image is ready (loaded without error)
   * @param nodeId The node id
   * @returns True if the image is ready
   */
  isReady(nodeId: string): boolean {
    const cache = this._nodeImageCache.get(nodeId);
    return cache != null && !cache.loading && cache.img != null;
  }

  /**
   * Get the loaded image element for a node
   * @param nodeId The node id
   * @returns The image element or null
   */
  getImage(nodeId: string): HTMLImageElement | null {
    const cache = this._nodeImageCache.get(nodeId);
    return cache?.img ?? null;
  }

  /**
   * Get the ready promise for a node's image
   * @param nodeId The node id
   * @returns The ready promise or undefined
   */
  getReadyPromise(nodeId: string): Promise<HTMLImageElement | null> | undefined {
    const cache = this._nodeImageCache.get(nodeId);
    return cache?.ready;
  }
}

/**
 * Load image asynchronously and store in provider
 * @param renderContext The render context
 * @param src The image source
 * @param callback Callback when image is loaded
 * @param node The node
 * @returns The image cache item
 */
export function loadImageAsync(
  renderContext: RenderContext,
  src: string,
  callback: () => void,
  node: any
): ImageCacheItem {
  if (!src) return loadImage(renderContext, '', callback, node);
  if (typeof src !== 'string') return {} as ImageCacheItem;
  const isLocalImage = src.startsWith('/');
  src = isLocalImage ? `${renderContext.publicPath}${src}` : src;
  return loadImage(renderContext, src, callback, node);
}

/**
 * Load and cache image for a node using the ImageProvider
 * This replaces the old pattern of storing _img on the node directly
 * @param renderContext The render context
 * @param node The node to load image for
 * @param src Optional source URL to use instead of node.src or node.fill
 */
export function loadImageCache(renderContext: RenderContext, node: any, src?: string): void {
  const imageProvider = renderContext.providers.image as ImageProvider;
  if (!imageProvider) {
    console.warn('ImageProvider not found in renderContext.providers');
    return;
  }

  const key = createImageCacheKey(node);
  const existingCache = imageProvider.getImageCache(node.id);

  if (existingCache && existingCache.cacheKey === key) {
    // Cache is still valid
    return;
  }

  try {
    let imageSrc = src || node.src;

    if (!src) {
      const fill = node.fill;
      if (isURLFill(fill)) {
        imageSrc = parseURLFill(fill);
      }
    }

    if (!imageSrc) {
      const fillType = resolveFillType(node.fill);
      if (fillType === FILL_TYPES.IMAGE) {
        const url = parseCSSURLString(node.fill);
        imageSrc = url;
      }
    }

    imageSrc = imageSrc || 'asset://placeholder';
    if (imageSrc) {
      const cache = loadImageAsync(
        renderContext,
        imageSrc,
        () => {
          renderContext.api.markNodeUpdate(node);
          renderContext.api.markNeedRender();
        },
        node
      );
      imageProvider.setImageCache(node.id, cache);
    } else {
      imageProvider.setImageCache(node.id, {
        cacheKey: key,
        src: '',
        loading: false,
        error: null,
        img: null,
      });
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * Get the image cache for a node from the provider
 * @param renderContext The render context
 * @param nodeId The node id
 * @returns The image cache item or undefined
 */
export function getNodeImageCache(
  renderContext: RenderContext,
  nodeId: string
): ImageCacheItem | undefined {
  const imageProvider = renderContext.providers.image as ImageProvider;
  return imageProvider?.getImageCache(nodeId);
}

/**
 * Check if a node's image is loading
 * @param renderContext The render context
 * @param node The node
 * @returns True if the image is loading
 */
export function isImageLoading(renderContext: RenderContext, node: any): boolean {
  const imageProvider = renderContext.providers.image as ImageProvider;
  return imageProvider?.isLoading(node.id) ?? false;
}

/**
 * Check if a node's image is ready
 * @param renderContext The render context
 * @param node The node
 * @returns True if the image is ready
 */
export function isImageReady(renderContext: RenderContext, node: any): boolean {
  const imageProvider = renderContext.providers.image as ImageProvider;
  return imageProvider?.isReady(node.id) ?? false;
}
